// ============================================================================
// DIRECTOR SIGNATORY FORM MODAL — FSA Signature Workflow
// ============================================================================
// Create/Edit master form for fsa.tbl_director_signatory. Client picker
// is async; effective_from/to date inputs carve the eligibility window
// the engagement panel picker filters by.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { fsaDirectorSignatoryService } from 'api/services/fsa/fsaSignatureService';
import { clientService } from 'api/services/clientService';
import type { DirectorSignatory } from 'api/types/fsa/signature.types';

export interface DirectorSignatoryFormData {
  clientId: string;
  signatoryName: string;
  din: string;
  designation: string;
  signatureBlockText: string;
  effectiveFrom: string;
  effectiveTo: string;
}

const INITIAL_FORM_DATA: DirectorSignatoryFormData = {
  clientId: '',
  signatoryName: '',
  din: '',
  designation: 'Director',
  signatureBlockText: '',
  effectiveFrom: '',
  effectiveTo: '',
};

const DESIGNATION_OPTIONS = [
  { value: 'Chairman', label: 'Chairman' },
  { value: 'Managing Director', label: 'Managing Director' },
  { value: 'Director', label: 'Director' },
  { value: 'Whole-time Director', label: 'Whole-time Director' },
  { value: 'Independent Director', label: 'Independent Director' },
  { value: 'Chief Financial Officer', label: 'Chief Financial Officer' },
  { value: 'Company Secretary', label: 'Company Secretary' },
];

const toIsoDate = (s: string | null | undefined): string =>
  s ? new Date(s).toISOString().slice(0, 10) : '';

interface DirectorSignatoryFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: DirectorSignatory | null;
  defaultClientId?: string;
  onSaved: () => void;
  onClose: () => void;
}

const DirectorSignatoryFormModal: React.FC<DirectorSignatoryFormModalProps> = ({ isOpen, mode, record, defaultClientId, onSaved, onClose }) => {
  const [formData, setFormData] = useState<DirectorSignatoryFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const recordRef = useRef(record);
  recordRef.current = record;
  const initialSnapshotRef = useRef('');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const data: DirectorSignatoryFormData = recordRef.current ? {
      clientId:           String(recordRef.current.clientId),
      signatoryName:      recordRef.current.signatoryName,
      din:                recordRef.current.din ?? '',
      designation:        recordRef.current.designation,
      signatureBlockText: recordRef.current.signatureBlockText ?? '',
      effectiveFrom:      toIsoDate(recordRef.current.effectiveFrom),
      effectiveTo:        toIsoDate(recordRef.current.effectiveTo),
    } : {
      ...INITIAL_FORM_DATA,
      clientId: defaultClientId ?? '',
    };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Async client loader.
  const loadClientOptions = useCallback(async (search: string) => {
    const resp = await clientService.getAll({ page: 1, pageSize: 50, searchTerm: search || undefined });
    return resp.data.map(c => ({
      value: String(c.id),
      label: c.legalName ? `${c.legalName} (${c.code})` : `${c.name} (${c.code})`,
    }));
  }, []);

  const seedOptions = useMemo(() => {
    if (record?.clientId && record?.clientLegalName) {
      return [{
        value: String(record.clientId),
        label: `${record.clientLegalName} (${record.clientCode ?? ''})`,
      }];
    }
    return undefined;
  }, [record]);

  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.clientId)                errors.clientId = 'Client is required';
    if (!formData.signatoryName.trim())    errors.signatoryName = 'Signatory name is required';
    if (!formData.designation.trim())      errors.designation = 'Designation is required';
    if (!formData.effectiveFrom)           errors.effectiveFrom = 'Effective from date is required';
    if (formData.signatoryName.length > 200) errors.signatoryName = 'Name cannot exceed 200 characters';
    if (formData.din && formData.din.length > 20) errors.din = 'DIN cannot exceed 20 characters';
    if (formData.effectiveTo && formData.effectiveFrom && formData.effectiveTo < formData.effectiveFrom) {
      errors.effectiveTo = 'Effective to must be on or after effective from';
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const payload = {
        clientId:           formData.clientId,
        signatoryName:      formData.signatoryName.trim(),
        din:                formData.din.trim() || null,
        designation:        formData.designation.trim(),
        signatureBlockText: formData.signatureBlockText.trim() || null,
        effectiveFrom:      formData.effectiveFrom,
        effectiveTo:        formData.effectiveTo || null,
        statusCode:         'ACTIVE',
      };
      if (mode === 'create') {
        await fsaDirectorSignatoryService.create(payload);
      } else if (recordRef.current) {
        await fsaDirectorSignatoryService.update(recordRef.current.id, {
          ...payload,
          rowVersion: recordRef.current.rowVersion,
        });
      }
      setIsSaving(false);
      notificationService.toast.success(`"${formData.signatoryName}" ${mode === 'create' ? 'created' : 'updated'} successfully.`);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      notificationService.toast.error(err?.response?.data?.message ?? 'Save failed', 6000);
    }
  }, [mode, formData, onSaved, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{mode === 'create' ? 'Add Director / Signatory' : 'Edit Director / Signatory'}</h2>
          <button className="modal-close" onClick={handleClose}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div className="form-group" data-field="clientId">
            <SyutSelect
              label="Client"
              required
              value={formData.clientId}
              onChange={(val) => { clearFieldError('clientId'); setFormData(prev => ({ ...prev, clientId: (val as string) || '' })); }}
              isAsync
              loadOptions={loadClientOptions}
              defaultOptions
              cacheOptions={false}
              seedOptions={seedOptions}
              error={getFieldError('clientId')}
              placeholder="Select client (company)…"
            />
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="signatoryName">
              <label className="form-label">Signatory Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('signatoryName') ? ' form-field-error' : ''}`}
                value={formData.signatoryName}
                onChange={(e) => { clearFieldError('signatoryName'); setFormData(prev => ({ ...prev, signatoryName: e.target.value })); }}
                maxLength={200}
              />
              {getFieldError('signatoryName') && <div className="form-error-message">{getFieldError('signatoryName')}</div>}
            </div>
            <div className="form-group" data-field="din">
              <label className="form-label">DIN</label>
              <input
                type="text"
                className={`form-input${getFieldError('din') ? ' form-field-error' : ''}`}
                value={formData.din}
                onChange={(e) => { clearFieldError('din'); setFormData(prev => ({ ...prev, din: e.target.value })); }}
                placeholder="Optional — for directors"
                maxLength={20}
              />
              {getFieldError('din') && <div className="form-error-message">{getFieldError('din')}</div>}
            </div>
          </div>

          <div className="form-grid-3">
            <div className="form-group" data-field="designation">
              <SyutSelect
                label="Designation"
                required
                value={formData.designation}
                onChange={(val) => { clearFieldError('designation'); setFormData(prev => ({ ...prev, designation: (val as string) || 'Director' })); }}
                options={DESIGNATION_OPTIONS}
                isClearable={false}
                error={getFieldError('designation')}
              />
            </div>
            <div className="form-group" data-field="effectiveFrom">
              <label className="form-label">Effective From <span className="text-danger">*</span></label>
              <input
                type="date"
                className={`form-input${getFieldError('effectiveFrom') ? ' form-field-error' : ''}`}
                value={formData.effectiveFrom}
                onChange={(e) => { clearFieldError('effectiveFrom'); setFormData(prev => ({ ...prev, effectiveFrom: e.target.value })); }}
              />
              {getFieldError('effectiveFrom') && <div className="form-error-message">{getFieldError('effectiveFrom')}</div>}
            </div>
            <div className="form-group" data-field="effectiveTo">
              <label className="form-label">Effective To</label>
              <input
                type="date"
                className={`form-input${getFieldError('effectiveTo') ? ' form-field-error' : ''}`}
                value={formData.effectiveTo}
                onChange={(e) => { clearFieldError('effectiveTo'); setFormData(prev => ({ ...prev, effectiveTo: e.target.value })); }}
                placeholder="Leave empty for currently active"
              />
              {getFieldError('effectiveTo') && <div className="form-error-message">{getFieldError('effectiveTo')}</div>}
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                Empty = currently active.
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Signature Block Text</label>
            <input
              type="text"
              className="form-input"
              value={formData.signatureBlockText}
              onChange={(e) => setFormData(prev => ({ ...prev, signatureBlockText: e.target.value }))}
              maxLength={500}
              placeholder="Optional per-signatory line printed below their name"
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>Save</button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message={`${mode === 'create' ? 'Create' : 'Update'} signatory "${formData.signatoryName}"?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default DirectorSignatoryFormModal;
